import { Injectable } from '@angular/core';

/**
 * Shared helper service for common passport-related operations
 * Used across multiple role-based components (Scheduler, Grower, Transloader, etc.)
 */
@Injectable({
  providedIn: 'root'
})
export class PassportHelperService {

  /**
   * Constant for "No Origin Field" display text
   * Used across filter dropdowns to allow filtering for passports with no origin
   */
  readonly NO_ORIGIN_FIELD_TEXT = 'No Origin Field';

  /**
   * Maps backend statusCode to tab category (legacy method for backward compatibility)
   * AWAITING_GROWER_ACTION → Load
   * IN_TRANSLOADING_PROCESS → Transloading
   * READY_FOR_SHUNTER → Shunting
   * WITH_GATEHOUSE → Transloading
   * RECEIVED_BY_RECEIVER → Shunting
   */
  mapStatusCodeToTab(statusCode: string): 'load' | 'shunting' | 'transloading' {
    const normalized = (statusCode || '').toUpperCase().trim();
    
    switch (normalized) {
      case 'AWAITING_GROWER_ACTION':
        return 'load';
      case 'IN_TRANSLOADING_PROCESS':
      case 'WITH_GATEHOUSE':
        return 'transloading';
      case 'READY_FOR_SHUNTER':
      case 'RECEIVED_BY_RECEIVER':
        return 'shunting';
      default:
        return 'load'; // Default to load tab for unknown status codes
    }
  }

  /**
   * Maps passport to tab category for Gatehouse and Scheduler personas
   * Uses transferFlag, statusCode, shuntingYardID, and transloadingStationID to determine tab.
   *
   * Priority order:
   * 0. Transferred (transferFlag=true AND statusCode is one of:
   *      AWAITING_GROWER_ACTION (StatusID=2),
   *      IN_TRANSLOADING_PROCESS (StatusID=4),
   *      WITH_GATEHOUSE (StatusID=10))
   *    When StatusID advances beyond these the load falls through to the normal routing below.
   * 1. Receiving (statusCode=RECEIVED_BY_RECEIVER, i.e. StatusID=8)
   * 2. Shunting (shuntingYardID is set)
   * 3. Transloading (transloadingStationID is set)
   * 4. Load (default)
   */
  mapPassportToTabCategory(
    statusCode: string | undefined,
    shuntingYardID: number | null | undefined,
    transloadingStationID: number | null | undefined,
    loadStatus?: string,
    transferFlag?: boolean
  ): 'load' | 'shunting' | 'transloading' | 'receiving' | 'transferred' {
    const normalizedStatus = (statusCode || '').toUpperCase().trim();

    const transferredStatuses = ['AWAITING_GROWER_ACTION', 'IN_TRANSLOADING_PROCESS', 'WITH_GATEHOUSE'];
    if (transferFlag === true && transferredStatuses.includes(normalizedStatus)) {
      return 'transferred';
    }

    // Priority 1: Receiving tab for RECEIVED_BY_RECEIVER status (StatusID=8)
    if (normalizedStatus === 'RECEIVED_BY_RECEIVER') {
      return 'receiving';
    }

    // Priority 2: Shunting tab if shuntingYardID is set
    if (shuntingYardID != null && shuntingYardID > 0) {
      return 'shunting';
    }

    // Priority 3: Transloading tab if transloadingStationID is set
    if (transloadingStationID != null && transloadingStationID > 0) {
      return 'transloading';
    }

    // Priority 4: Load tab (default)
    return 'load';
  }

  /**
   * Get persona ID based on persona/role name
   * PersonaID mappings: Admin (1), Grower (2), Transloader (3), 
   * Receiver (4), Shunter (5), Scheduler (6), Gatehouse (7)
   */
  getRoleIdForPersona(persona: string): number {
    const roleMap: Record<string, number> = {
      'admin': 1,
      'grower': 2,
      'transloader': 3,
      'receiver': 4,
      'shunter': 5,
      'shunting': 5,
      'scheduler': 6,
      'gate house': 7,
      'gatehouse': 7,
      'audit': 8,
      'auditor': 8
    };
    
    const normalized = (persona || '').toLowerCase();
    if (roleMap[normalized]) return roleMap[normalized];
    
    // Check partial matches using ordered list
    const partialMatches: Array<{ keyword: string; roleId: number }> = [
      { keyword: 'admin', roleId: 1 },
      { keyword: 'grower', roleId: 2 },
      { keyword: 'transload', roleId: 3 },
      { keyword: 'receiver', roleId: 4 },
      { keyword: 'shunt', roleId: 5 },
      { keyword: 'schedule', roleId: 6 },
      { keyword: 'gate', roleId: 7 },
      { keyword: 'audit', roleId: 8 }
    ];
    
    for (const match of partialMatches) {
      if (normalized.includes(match.keyword)) {
        return match.roleId;
      }
    }
    
    return 2; // Default to Grower (PersonaID 2)
  }

  /**
   * Format origin name with type prefix (Field:, Storage:, or Plant:)
   */
  formatOriginWithType(originName?: string, rawSourceType?: string): string {
    if (!originName) return '';
    if (rawSourceType === '2') return `Field: ${originName}`;
    if (rawSourceType === '1') return `Storage: ${originName}`;
    if (rawSourceType === '4') return `Plant: ${originName}`;
    return originName;
  }

  /**
   * Add unique option to array if not already present
   */
  addUniqueOption(options: string[], value: string | undefined): void {
    if (value && !options.includes(value)) {
      options.push(value);
    }
  }

  /**
   * Create filter definition with sorted options
   */
  createFilterDefinition(id: string, label: string, options: string[]): { id: string; label: string; options: string[] } {
    return {
      id,
      label,
      options: [...options].sort((a, b) => a.localeCompare(b))
    };
  }

  /**
   * Check if a value matches active filter
   * Handles "No Origin Field" specially for origin filter - matches empty/null values
   */
  matchesFilter(activeFilters: any, filterKey: string, value: string | undefined): boolean {
    const filterValues = activeFilters[filterKey];
    if (!filterValues?.length) return true;
    
    // Special handling for origin filter with "No Origin Field" option
    if (filterKey === 'origin' && filterValues.includes(this.NO_ORIGIN_FIELD_TEXT)) {
      // If value is empty/null/whitespace, it matches "No Origin Field"
      if (!value?.trim()) {
        return true;
      }
      // Also check if the actual value is in the filter (for multi-select)
      return filterValues.includes(value);
    }
    
    return filterValues.includes(value || '');
  }

  /**
   * Generate simple variety + haulier filters from passport records
   * Used by Shunting and Receiver components
   */
  generateSimpleFilters(records: Array<{ variety?: string; haulier?: string }>): Array<{ id: string; label: string; options: string[] }> {
    const varieties = new Set<string>();
    const hauliers = new Set<string>();
    
    for (const r of records) {
      if (r.variety) varieties.add(r.variety);
      if (r.haulier) hauliers.add(r.haulier);
    }

    return [
      { id: 'variety', label: 'Variety', options: Array.from(varieties).sort((a, b) => a.localeCompare(b)) },
      { id: 'haulier', label: 'Haulier', options: Array.from(hauliers).sort((a, b) => a.localeCompare(b)) }
    ];
  }

  /**
   * Determine the visual style state for a passport based on its properties
   * Used by Growers and Transloading components
   */
  getStyleState(details: { isCancelled?: boolean; hasSeenPcrChanges?: boolean } | undefined): 'default' | 'pcr-change' | 'cancelled' | 'state-2' {
    if (!details) return 'default';
    
    // Cancelled state takes priority
    if (details.isCancelled === true) {
      return 'cancelled';
    }
    
    // PCR change flag (unseen changes)
    if (details.hasSeenPcrChanges === false) {
      return 'pcr-change';
    }
    
    return 'default';
  }

  /**
   * Build standard search field array for passport records
   * Used by Gatehouse, Scheduler, and Transloading visibleRecords
   */
  buildSearchFields(record: any, details: any): string[] {
    // Use "No Origin Field" as searchable text when origin is empty
    const originSearchValue = details?.origin?.trim() ? details.origin : this.NO_ORIGIN_FIELD_TEXT;
    
    return [
      record.id || '',
      details?.loadNumber || '',
      details?.variety || '',
      details?.vendorName || '',
      details?.vendorNumber || '',
      details?.apn || '',
      details?.apnName || '',
      details?.haulier || '',
      details?.destinationWeighbridge || details?.destination || '',
      originSearchValue,
      details?.bulkerMain || details?.bulkerNumber || '',
      details?.authorisedBy || '',
      details?.haccpCheck || '',
      details?.statusCode || ''
    ];
  }

  /**
   * Get sort values for a given column
   * Used by Growers and Transloading components for table sorting
   */
  getSortValues(column: string, a: any, b: any, aDetails: any, bDetails: any): { valA: any; valB: any } {
    let valA: any = '';
    let valB: any = '';

    switch (column) {
      case 'loadNumber': valA = aDetails?.loadNumber || a.id; valB = bDetails?.loadNumber || b.id; break;
      case 'scheduled': valA = aDetails?.dueDateTime || ''; valB = bDetails?.dueDateTime || ''; break;
      case 'vendorCombined': 
        valA = (aDetails?.vendorNumber || '') + (aDetails?.vendorName || ''); 
        valB = (bDetails?.vendorNumber || '') + (bDetails?.vendorName || ''); 
        break;
      case 'variety': valA = aDetails?.variety || ''; valB = bDetails?.variety || ''; break;
      case 'origin': valA = aDetails?.origin || ''; valB = bDetails?.origin || ''; break;
      case 'destination':
        valA = aDetails?.destinationWeighbridge || aDetails?.destination || '';
        valB = bDetails?.destinationWeighbridge || bDetails?.destination || '';
        break;
      case 'haulier': valA = aDetails?.haulier || ''; valB = bDetails?.haulier || ''; break;
      case 'apnName': valA = aDetails?.apnName || ''; valB = bDetails?.apnName || ''; break;
      case 'bulkerNumber': valA = aDetails?.bulkerMain || aDetails?.bulkerNumber || ''; valB = bDetails?.bulkerMain || bDetails?.bulkerNumber || ''; break;
      case 'planned': valA = aDetails?.plannedDateTime || ''; valB = bDetails?.plannedDateTime || ''; break;
      case 'vendorName': valA = aDetails?.vendorName || ''; valB = bDetails?.vendorName || ''; break;
      case 'vendorNumber': valA = aDetails?.vendorNumber || ''; valB = bDetails?.vendorNumber || ''; break;
    }

    return { valA, valB };
  }

  /**
   * Compare two values for sorting
   * Used by Growers and Transloading components
   */
  compareValues(a: any, b: any, direction: 'asc' | 'desc'): number {
    if (a < b) return direction === 'asc' ? -1 : 1;
    if (a > b) return direction === 'asc' ? 1 : -1;
    return 0;
  }

  /**
   * Calculate total pages for pagination
   * Used by Growers and Transloading components
   */
  getTotalPages(recordsLength: number, itemsPerPage: number): number {
    return Math.ceil(recordsLength / itemsPerPage) || 1;
  }

  /**
   * Bulker display: Grower/Transloader use detail value (trailerNumber); other personas prefer main (trailerMain).
   */
  getDisplayBulker(
    details: { bulkerMain?: string; bulkerNumber?: string } | null | undefined,
    preferBulkerMain: boolean
  ): string {
    if (!details) {
      return '';
    }
    if (preferBulkerMain) {
      return details.bulkerMain?.trim() || details.bulkerNumber?.trim() || '';
    }
    return details.bulkerNumber?.trim() || '';
  }

  /**
   * Build header object for comments/history panels
   * Used by Transloading, Receiver, and Growers components
   */
  buildPanelHeader(
    details: any,
    dateTimeHelper: any,
    preferBulkerMain = true
  ): { loadNumber: string; variety: string; haulier: string; scheduledText: string; origin: string; destination: string; apnName: string; vendorName: string; vendorNumber: string; bulkerNumber: string } | undefined {
    if (!details) return undefined;
    
    return {
      loadNumber: details.loadNumber || '',
      variety: details.variety || '',
      haulier: details.haulier || '',
      scheduledText: dateTimeHelper.formatScheduledText(details.dueDateTime || '') || '',
      origin: details.origin || '',
      destination: details.destinationWeighbridge || details.destination || '',
      apnName: details.apnName || '',
      vendorName: details.vendorName || '',
      vendorNumber: details.vendorNumber || '',
      bulkerNumber: this.getDisplayBulker(details, preferBulkerMain)
    };
  }

  /**
   * Safely load passport data from IndexedDB, returning empty array on error
   * Used by Transloading and Growers components
   */
  async safeLoadPassportData(
    passportId: number,
    dataType: string,
    loadFn: (id: number) => Promise<any[]>
  ): Promise<any[]> {
    try {
      return await loadFn(passportId) || [];
    } catch (error) {
      console.warn(`Failed to load ${dataType} for passport ${passportId} from IndexedDB:`, error);
      return [];
    }
  }

  /**
   * Build Origin/Destination/APN filter definitions from passport records
   * Used by multiple persona components to generate filter options
   * @param records Array of passport records to extract filter options from
   * @param detailsStore Store to get detailed passport information
   * @returns Array of filter definitions with sorted options
   */
  buildOriginDestinationApnFilters(
    records: Array<{ id: string }>,
    detailsStore: any
  ): Array<{ id: string; label: string; options: string[] }> {
    const originOptions: string[] = [];
    const destinationOptions: string[] = [];
    const apnOptions: string[] = [];
    let hasEmptyOrigin = false;

    for (const rec of records) {
      const d = detailsStore.get(rec.id);
      if (!d) continue;
      
      // Check if this record has an empty origin
      if (d.origin?.trim()) {
        this.addUniqueOption(originOptions, d.origin);
      } else {
        hasEmptyOrigin = true;
      }
      
      const destinationValue = d.destinationWeighbridge || d.destination;
      this.addUniqueOption(destinationOptions, destinationValue);
      const apnValue = d.apnName || d.apn;
      this.addUniqueOption(apnOptions, apnValue);
    }

    // Add "No Origin Field" option if any records have empty origins
    if (hasEmptyOrigin) {
      originOptions.unshift(this.NO_ORIGIN_FIELD_TEXT);
    }

    return [
      this.createFilterDefinitionWithNoOriginFirst('origin', 'Origin', originOptions),
      this.createFilterDefinition('destination', 'Destination', destinationOptions),
      this.createFilterDefinition('apnName', 'APN/Name', apnOptions)
    ];
  }

  /**
   * Create filter definition with sorted options, keeping "No Origin Field" first if present
   */
  private createFilterDefinitionWithNoOriginFirst(id: string, label: string, options: string[]): { id: string; label: string; options: string[] } {
    const hasNoOrigin = options.includes(this.NO_ORIGIN_FIELD_TEXT);
    const otherOptions = options.filter(o => o !== this.NO_ORIGIN_FIELD_TEXT);
    const sortedOthers = [...otherOptions].sort((a, b) => a.localeCompare(b));
    
    return {
      id,
      label,
      options: hasNoOrigin ? [this.NO_ORIGIN_FIELD_TEXT, ...sortedOthers] : sortedOthers
    };
  }

  /**
   * Get default empty Origin/Destination/APN filter definitions
   * Used as fallback when filter building fails
   */
  getDefaultOriginDestinationApnFilters(): Array<{ id: string; label: string; options: string[] }> {
    return [
      { id: 'origin', label: 'Origin', options: [] },
      { id: 'destination', label: 'Destination', options: [] },
      { id: 'apnName', label: 'APN/Name', options: [] }
    ];
  }

}



